import React, { useState } from 'react';
import { Modal, Form, Input, message, Alert } from 'antd';
import { LockOutlined, EyeInvisibleOutlined, EyeTwoTone } from '@ant-design/icons';
import apiService from '../services/api.service';
import { useAuth } from '../contexts/AuthContext';

interface ForcePasswordChangeProps {
  visible: boolean;
}

const ForcePasswordChange: React.FC<ForcePasswordChangeProps> = ({ visible }) => {
  const [form] = Form.useForm();
  const [loading, setLoading] = useState(false);
  const { clearMustChangePassword, user } = useAuth();

  const handleSubmit = async () => {
    try {
      const values = await form.validateFields();
      setLoading(true);

      await apiService.changeMyPassword(values.password);

      message.success('Password aggiornata con successo!');
      clearMustChangePassword();
      form.resetFields();
    } catch (error: any) {
      if (error.errorFields) {
        return;
      }
      message.error(error.response?.data?.error || 'Errore nel cambio password');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Modal
      title={
        <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
          <div
            style={{
              width: 40,
              height: 40,
              borderRadius: '50%',
              background: 'linear-gradient(135deg, #EB2ADD 0%, #d926b9 100%)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
            }}
          >
            <LockOutlined style={{ fontSize: 20, color: 'white' }} />
          </div>
          <span style={{ fontSize: 18, fontWeight: 600 }}>Cambio Password Obbligatorio</span>
        </div>
      }
      open={visible}
      onOk={handleSubmit}
      closable={false}
      maskClosable={false}
      keyboard={false}
      okText="Aggiorna Password"
      cancelButtonProps={{ style: { display: 'none' } }}
      confirmLoading={loading}
      width={480}
      centered
    >
      <Alert
        message="Primo accesso rilevato"
        description={
          <>
            Ciao <strong>{user?.firstName}</strong>, devi cambiare la tua password temporanea
            prima di poter accedere al sistema.
          </>
        }
        type="warning"
        showIcon
        style={{ marginBottom: 24, marginTop: 16 }}
      />

      <Form form={form} layout="vertical" autoComplete="off">
        <Form.Item
          name="password"
          label="Nuova Password"
          rules={[
            { required: true, message: 'Password obbligatoria' },
            { min: 6, message: 'La password deve avere almeno 6 caratteri' },
          ]}
        >
          <Input.Password
            placeholder="Inserisci la nuova password"
            size="large"
            iconRender={(visible) =>
              visible ? <EyeTwoTone /> : <EyeInvisibleOutlined />
            }
          />
        </Form.Item>

        <Form.Item
          name="confirmPassword"
          label="Conferma Password"
          dependencies={['password']}
          rules={[
            { required: true, message: 'Conferma la password' },
            ({ getFieldValue }) => ({
              validator(_, value) {
                if (!value || getFieldValue('password') === value) {
                  return Promise.resolve();
                }
                return Promise.reject(new Error('Le password non coincidono'));
              },
            }),
          ]}
        >
          <Input.Password
            placeholder="Ripeti la nuova password"
            size="large"
            iconRender={(visible) =>
              visible ? <EyeTwoTone /> : <EyeInvisibleOutlined />
            }
          />
        </Form.Item>
      </Form>

      <div
        style={{
          background: '#f8fafc',
          padding: 12,
          borderRadius: 8,
          fontSize: 13,
          color: '#64748b',
        }}
      >
        <strong>Requisiti password:</strong>
        <ul style={{ margin: '8px 0 0 0', paddingLeft: 20 }}>
          <li>Minimo 6 caratteri</li>
          <li>Non usare la password temporanea (12345678)</li>
        </ul>
      </div>
    </Modal>
  );
};

export default ForcePasswordChange;
